// ─────────────────────────────────────────────────────────────────────────────
// Design system partagé — source de vérité du "nouveau look" (référence Dashboard.js)
//
// Il n'y a PAS de thème MUI global dans cette app : le style est appliqué par
// composant via la prop `sx`. Ce fichier centralise les tokens du design moderne
// pour que tous les écrans partagent exactement les mêmes cartes / couleurs / fond,
// au lieu de recopier des valeurs à la main.
//
// Usage typique :
//   import { pageCanvas, cardBase, iconTile, PALETTE, INK } from '../theme/designSystem';
//   <Box sx={pageCanvas}> ... <Card sx={cardBase}> ...
//   <Box sx={iconTile(primaryColor)}><Icon sx={{ color: primaryColor }} /></Box>
//
// Les accents peuvent venir de PALETTE (palette plate façon Tailwind) OU du thème
// club via useClubTheme() (primaryColor / secondaryColor) — les deux se combinent
// avec alpha() pour les fonds teintés.
// ─────────────────────────────────────────────────────────────────────────────
import { alpha } from '@mui/material/styles';

// Palette d'accents plats (façon Tailwind), identique à Dashboard.js
export const PALETTE = {
  indigo: '#6366f1',
  sky: '#0ea5e9',
  amber: '#f59e0b',
  emerald: '#10b981',
  pink: '#ec4899',
  red: '#ef4444',
  teal: '#14b8a6',
  violet: '#8b5cf6',
};

// Couleur de texte des titres (encre) et fond de page (canvas)
export const INK = '#1a1a2e';
export const CANVAS = '#f8f9fb';

// Enveloppe de page : à poser sur le <Box> racine d'un écran.
// (Le Layout applique déjà ce fond globalement ; utile pour les écrans qui
//  veulent un padding/fond explicite sans dépendre du Layout.)
export const pageCanvas = {
  minHeight: '100vh',
  background: CANVAS,
  p: { xs: 2, md: 3 },
};

// Style de carte de référence, copié à l'identique de Dashboard.js.
// À étaler dans une <Card sx={{ ...cardBase }}> à la place d'un <Paper elevation>.
export const cardBase = {
  borderRadius: '16px',
  border: '1px solid',
  borderColor: 'rgba(0,0,0,0.06)',
  background: '#fff',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  transition: 'all 0.2s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    borderColor: 'rgba(0,0,0,0.1)',
  },
};

// Variante sans effet de survol (cartes non cliquables / statiques).
export const cardStatic = {
  ...cardBase,
  transition: 'none',
  '&:hover': undefined,
};

// Tuile d'icône teintée et arrondie (fond alpha + icône colorée au centre).
// color : couleur d'accent (primaryColor du club ou une valeur de PALETTE).
// size  : côté du carré en px (48 par défaut, comme les KPI du Dashboard).
export const iconTile = (color, size = 48) => ({
  width: size,
  height: size,
  borderRadius: size >= 44 ? '14px' : '12px',
  background: alpha(color, 0.1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  transition: 'transform 0.2s ease',
});

// Titre de section : poids lourd + encre foncée (h6-like du Dashboard).
export const sectionTitle = {
  fontWeight: 700,
  color: INK,
  fontSize: '1.05rem',
};

// Pastille / chip teintée à partir d'une couleur d'accent.
export const tintedChip = (color) => ({
  bgcolor: alpha(color, 0.08),
  color,
  fontWeight: 600,
  '& .MuiChip-icon': { color },
});

// En-tête de tableau teinté (à poser sur le TableHead .MuiTableCell ou une Box).
// Remplace les headers pleins (#1976d2 / #F25F29) par un fond alpha discret.
export const tableHeadTint = (color) => ({
  bgcolor: alpha(color, 0.08),
  '& .MuiTableCell-root': {
    fontWeight: 700,
    color: INK,
    borderBottom: '1px solid rgba(0,0,0,0.06)',
  },
});
